import { computed, type Ref } from "vue";
import { useDSFormField } from "@/src/design_system/form_elements/shared/composables/useDSFormField";
import { DS_URL_FIELD_CLASSES } from "@/src/design_system/form_elements/url_field/constants/classes";
import type { DSUrlFieldModelValue, DSUrlFieldProps } from "@/src/design_system/form_elements/url_field/types";
import { DS_STATE_CLASSES } from "@/src/design_system/shared/constants/stateClasses";
import { ensureUrlProtocol } from "@/src/shared/utils/url";
import { getSafeUrl } from "@/src/utils/safeNavigation";

interface UseDSUrlFieldOptions {
  props: Readonly<DSUrlFieldProps>;
  modelValue: Ref<DSUrlFieldModelValue>;
}

export const useDSUrlField = ({ props, modelValue }: UseDSUrlFieldOptions) => {
  const dsFormField = useDSFormField<DSUrlFieldModelValue>({
    name: computed(() => props.name),
    errorMessages: computed(() => props.errorMessages),
    value: modelValue,
    rules: computed(() => props.rules),
  });

  const hasErrors = computed(() => !!dsFormField.errorMessages.value.length);
  const isEditable = computed(() => !props.disabled && !props.readonly);
  const isClearable = computed(() => isEditable.value && props.clearable);
  const copyValue = computed(() => modelValue.value ?? "");
  const openHref = computed(() => {
    const href = ensureUrlProtocol(copyValue.value);
    return href && getSafeUrl(href) ? href : null;
  });
  const rootClasses = computed(() => ({
    [DS_URL_FIELD_CLASSES.Root]: true,
    [DS_URL_FIELD_CLASSES.Readonly]: props.readonly,
    [DS_STATE_CLASSES.InlineEdit]: props.inlineEdit,
    [DS_STATE_CLASSES.Disabled]: props.disabled,
    [DS_STATE_CLASSES.Error]: hasErrors.value,
  }));

  const onInput = () => {
    dsFormField.clearFieldErrors();
  };

  const onClear = () => {
    if (!isEditable.value) {
      return;
    }

    modelValue.value = "";
    dsFormField.clearFieldErrors();
  };

  return {
    copyValue,
    openHref,
    errorMessages: dsFormField.errorMessages,
    hasErrors,
    isClearable,
    onClear,
    onInput,
    rootClasses,
  };
};
